import {
  Container,
  ExampleVariables,
  Form,
  ButtonAdded,
  Button
} from "./styles";
import { AuthContextType, useAuth } from "../../auth/auth-provider";
import { IconFile, IconImage, IconText, IconVideo, spaik } from "../../assets";
import { Text } from "../Text";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { TextArea } from "../Interactions/components/Exceptions/styles";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import { v } from "../../util";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState, useEffect } from "react";
import useWhatsAppTemplatesApi, {
  TemplateDto,
  TemplateInputDto,
  ComponentDto,
  ButtonDto,
  HeaderFormat
} from "../WhatsAppTemplates/use-whatsAppTemplates";
import { useToast } from "../toast-provider";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { setShowModalCreateTemplate } from "../../redux/slices/templates";
import { ProgressSpinner } from "primereact/progressspinner";

type ButtonTypeValue = "QUICK_REPLY" | "COPY_CODE" | "URL";

type ButtonForm = {
  type: ButtonTypeValue;
  text: string;
  url?: string;
  code?: string;
};

type CreateTemplateFormValues = {
  name: string;
  category: string;
  language: string;
  body: string;
  headerText?: string;
  footerText?: string;
  message_send_ttl_seconds: number | null;
  buttons: ButtonForm[];
  bodySamples: { value: string }[];
  headerSamples: { value: string }[];
};

type UpdateWhatsAppTemplateProps = {
  mode?: "create" | "edit";
  template?: TemplateDto | null;
  onSuccess?: () => void | Promise<void>;
};

const UpdateWhatsAppTemplate = ({
  mode = "create",
  template = null,
  onSuccess
}: UpdateWhatsAppTemplateProps) => {
  const { createTemplate, editTemplate } = useWhatsAppTemplatesApi();
  const toast = useToast();
  const dispatch = useAppDispatch();
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const botIdActive = useAppSelector(
    (state) => state.flowInteractions.botIdActive
  );
  const BOT_ID_KEY = "botIdActive";
  const effectiveBotId =
    botIdActive > 0
      ? botIdActive
      : Number(localStorage.getItem(BOT_ID_KEY) || 0);

  const extractPositionalPlaceholders = (txt?: string) => {
    const s = txt ?? "";
    const re = /{{\s*(\d+)\s*}}/g;
    const set = new Set<number>();
    let m: RegExpExecArray | null;

    while ((m = re.exec(s))) set.add(Number(m[1]));

    return Array.from(set).sort((a, b) => a - b);
  };

  const createTemplateSchema = v
    .object({
      name: v.string().superRefine(v.nonempty()),
      category: v.string().superRefine(v.nonempty()),
      language: v.string().superRefine(v.nonempty()),
      body: v.string().superRefine(v.nonempty()),
      headerText: v.string().optional(),
      message_send_ttl_seconds: v.number().nullable().optional(),
      footerText: v.string().optional(),
      bodySamples: v.array(
        v.object({
          value: v.string().optional()
        })
      ),
      headerSamples: v.array(
        v.object({
          value: v.string().optional()
        })
      ),
      buttons: v.array(
        v.object({
          type: v.string().superRefine(v.nonempty()),
          text: v.string().optional(),
          url: v.string().optional(),
          code: v.string().optional()
        })
      )
    })
    .superRefine((data, ctx) => {
      const isAlnum = (s: string) => /^[A-Za-z0-9]+$/.test(s);

      (data.buttons ?? []).forEach((b, i) => {
        const type = (b.type ?? "").trim();

        if (!["QUICK_REPLY", "COPY_CODE", "URL"].includes(type)) {
          ctx.addIssue({ code: "custom", path: ["buttons", i, "type"] });
          return;
        }

        if (type !== "COPY_CODE" && !(b.text ?? "").trim()) {
          ctx.addIssue({ code: "custom", path: ["buttons", i, "text"] });
        }

        if (type === "URL" && !(b.url ?? "").trim()) {
          ctx.addIssue({ code: "custom", path: ["buttons", i, "url"] });
        }

        if (type === "COPY_CODE") {
          const code = (b.code ?? "").trim();

          if (code && !isAlnum(code)) {
            ctx.addIssue({ code: "custom", path: ["buttons", i, "code"] });
          }
        }
      });

      let stopped = false;
      const stopAt = (path: (string | number)[]) => {
        if (stopped) return;
        stopped = true;
        ctx.addIssue({ code: "custom", path });
      };

      const validatePositionalSamples = (
        text: string | undefined,
        samples: { value?: string }[] | undefined,
        samplesField: "bodySamples" | "headerSamples"
      ) => {
        const needed = extractPositionalPlaceholders(text);
        if (!needed.length) return;

        for (const n of needed) {
          const idx = n - 1;
          const value = (samples?.[idx]?.value ?? "").trim();

          if (!value) {
            stopAt([samplesField, idx, "value"]);
            break;
          }
        }
      };

      if (!stopped) {
        validatePositionalSamples(data.body, data.bodySamples, "bodySamples");
      }

      if (!stopped && (data.headerText ?? "").trim()) {
        validatePositionalSamples(
          data.headerText,
          data.headerSamples,
          "headerSamples"
        );
      }
    });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    getValues,
    reset
  } = useForm<CreateTemplateFormValues>({
    defaultValues: {
      name: "",
      category: "",
      language: "",
      body: "",
      headerText: "",
      footerText: "",
      message_send_ttl_seconds: null,
      buttons: [],
      bodySamples: [],
      headerSamples: []
    },
    resolver: zodResolver(createTemplateSchema),
    shouldFocusError: true
  });

  const { user } = useAuth() as AuthContextType;
  const [header, setHeader] = useState<HeaderFormat | null>(null);

  const categoryOptions = [
    { label: "Utilidade", value: "UTILITY" },
    { label: "Marketing", value: "MARKETING" }
  ];

  const languageOptions = [
    { label: "Espanhol", value: "es_ES" },
    { label: "Inglês", value: "en_US" },
    { label: "Português", value: "pt_BR" }
  ];

  const validityMinutesOptions = [
    { label: "Usar tempo padrão (Meta)", value: null },
    { label: "2 minutos", value: 120 },
    { label: "10 minutos", value: 600 },
    { label: "30 minutos", value: 1800 },
    { label: "1 hora", value: 3600 },
    { label: "2 horas", value: 7200 },
    { label: "5 horas", value: 18000 },
    { label: "12 horas", value: 43200 }
  ];

  const {
    fields: buttonFields,
    append: appendButton,
    remove: removeButton,
    replace: replaceButtons
  } = useFieldArray({
    control,
    name: "buttons"
  });

  const {
    fields: bodySampleFields,
    append: appendBodySample,
    remove: removeBodySample,
    replace: replaceBodySamples
  } = useFieldArray({ control, name: "bodySamples" });

  const {
    fields: headerSampleFields,
    append: appendHeaderSample,
    remove: removeHeaderSample,
    replace: replaceHeaderSamples
  } = useFieldArray({ control, name: "headerSamples" });

  const addVarTo = (
    fieldName: "body" | "headerText",
    sampleName: "bodySamples" | "headerSamples"
  ) => {
    if (fieldName === "headerText" && headerSampleFields.length >= 1) {
      toast?.current?.show({
        life: 3000,
        severity: "warn",
        summary: "Atenção",
        detail: "Só é permitido uma variável no header."
      });
      return;
    }

    const nextIndex =
      (sampleName === "bodySamples"
        ? bodySampleFields.length
        : headerSampleFields.length) + 1;

    const current = (watch(fieldName) ?? "").trimEnd();
    const nextText = current
      ? `${current} {{${nextIndex}}}`
      : `{{${nextIndex}}}`;
    setValue(fieldName, nextText);

    const newSample = { value: "" };

    if (sampleName === "bodySamples") appendBodySample(newSample);
    else appendHeaderSample(newSample);
  };

  const toggleHeader = (format: HeaderFormat) => {
    setHeader((prev) => {
      const next = prev === format ? null : format;

      if (prev === "TEXT" && next !== "TEXT") {
        setValue("headerText", "");
        setValue("headerSamples", []);
      }

      return next;
    });
  };

  const buttonTypeOptions = [
    { label: "Personalizado", value: "QUICK_REPLY" },
    { label: "Copiar", value: "COPY_CODE" },
    { label: "URL", value: "URL" }
  ];

  useEffect(() => {
    if (mode !== "edit" || !template) return;

    setValue("name", template.name ?? "");
    setValue("category", template.category ?? "");
    setValue("language", template.language ?? "");

    const comps = template.components ?? [];

    const headerComp = comps.find((c) => c.type === "HEADER");
    const bodyComp = comps.find((c) => c.type === "BODY");
    const footerComp = comps.find((c) => c.type === "FOOTER");
    const buttonsComp = comps.find((c) => c.type === "BUTTONS");

    if (headerComp?.format) setHeader(headerComp.format);
    else setHeader(null);

    setValue("headerText", headerComp?.text ?? "");
    setValue("body", bodyComp?.text ?? "");
    setValue("footerText", footerComp?.text ?? "");

    const btnsForm: ButtonForm[] =
      buttonsComp?.buttons?.map((b) => {
        if (b.type === "COPY_CODE") {
          return {
            type: "COPY_CODE",
            text: "",
            code: b.example?.[0] ?? ""
          };
        }

        if (b.type === "URL") {
          return {
            type: "URL",
            text: b.text ?? "",
            url: b.url ?? ""
          };
        }

        return {
          type: "QUICK_REPLY",
          text: b.text ?? ""
        };
      }) ?? [];

    replaceButtons(btnsForm);

    const positionalBody = bodyComp?.example?.body_text?.[0] ?? [];
    replaceBodySamples(positionalBody.map((v) => ({ value: v })));

    if (headerComp?.format === "TEXT") {
      const positionalHeader = headerComp?.example?.header_text ?? [];
      replaceHeaderSamples(positionalHeader.map((v) => ({ value: v })));
    } else {
      replaceHeaderSamples([]);
    }
  }, [
    mode,
    template,
    setValue,
    replaceButtons,
    replaceBodySamples,
    replaceHeaderSamples
  ]);

  const normalizePositional = (
    text: string,
    samples: { value?: string }[]
  ): { text: string; samples: { value: string }[] } => {
    const re = /{{\s*(\d+)\s*}}/g;

    const nums = new Set<number>();
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) nums.add(Number(m[1]));
    const oldNums = Array.from(nums).sort((a, b) => a - b);

    if (!oldNums.length) {
      return { text, samples: [] };
    }

    const map = new Map<number, number>();
    oldNums.forEach((n, idx) => map.set(n, idx + 1));

    const nextText = text.replace(re, (_, raw) => {
      const n = Number(raw);
      const mapped = map.get(n);
      return mapped ? `{{${mapped}}}` : "";
    });

    const nextSamples: { value: string }[] = oldNums.map((oldN) => {
      const v = (samples?.[oldN - 1]?.value ?? "").toString();
      return { value: v };
    });

    return { text: nextText, samples: nextSamples };
  };

  const sameSamples = (a: { value?: string }[], b: { value?: string }[]) => {
    if ((a?.length ?? 0) !== (b?.length ?? 0)) return false;
    for (let i = 0; i < (a?.length ?? 0); i++) {
      if ((a[i]?.value ?? "") !== (b[i]?.value ?? "")) return false;
    }
    return true;
  };

  const syncPositional = (
    fieldName: "body" | "headerText",
    sampleName: "bodySamples" | "headerSamples",
    replaceFn: (value: { value: string }[]) => void
  ) => {
    const rawText = (getValues(fieldName) ?? "") as string;
    const rawSamples = (getValues(sampleName) ?? []) as { value?: string }[];

    const { text: nextText, samples: nextSamples } = normalizePositional(
      rawText,
      rawSamples
    );

    if (nextText !== rawText) {
      setValue(fieldName, nextText, { shouldDirty: true, shouldTouch: true });
    }

    if (!sameSamples(rawSamples, nextSamples)) {
      replaceFn(nextSamples);
    }
  };

  const bodyText = watch("body");
  const headerTextValue = watch("headerText");

  useEffect(() => {
    syncPositional("body", "bodySamples", replaceBodySamples);
  }, [bodyText]);

  const replaceAllHeaderVarsTo1 = (text: string) =>
    text.replace(/{{\s*\d+\s*}}/g, "{{1}}");

  useEffect(() => {
    if (header !== "TEXT") return;

    const raw = (getValues("headerText") ?? "") as string;
    const nums = extractPositionalPlaceholders(raw);

    const invalid = nums.length > 0 && (nums.length > 1 || nums[0] !== 1);

    if (invalid) {
      toast?.current?.show({
        life: 3000,
        severity: "warn",
        summary: "Atenção",
        detail: "No header só é permitido {{1}}."
      });

      const fixedText = replaceAllHeaderVarsTo1(raw);

      const firstNonEmpty =
        (getValues("headerSamples") ?? [])
          .map((s: any) => (s?.value ?? "").trim())
          .find(Boolean) ?? "";

      setValue("headerText", fixedText, {
        shouldDirty: true,
        shouldTouch: true
      });
      replaceHeaderSamples([{ value: firstNonEmpty }]);
      return;
    }

    syncPositional("headerText", "headerSamples", replaceHeaderSamples);
  }, [headerTextValue, header, getValues, setValue, replaceHeaderSamples]);

  const removeVarAt = (
    fieldName: "body" | "headerText",
    sampleName: "bodySamples" | "headerSamples",
    replaceFn: (value: { value: string }[]) => void,
    index: number
  ) => {
    const n = index + 1;

    const raw = (getValues(fieldName) ?? "") as string;

    const next = raw
      .replace(new RegExp(`{{\\s*${n}\\s*}}`, "g"), "")
      .replace(/[ ]{2,}/g, " ");

    setValue(fieldName, next, { shouldDirty: true, shouldTouch: true });

    syncPositional(fieldName, sampleName, replaceFn);
  };

  useEffect(() => {
    if (mode !== "edit" || !template) {
      reset({
        name: "",
        category: "",
        language: "",
        body: "",
        headerText: "",
        footerText: "",
        message_send_ttl_seconds: null,
        buttons: [],
        bodySamples: [],
        headerSamples: []
      });

      setHeader(null);
      return;
    }
  }, [mode, template, reset]);

  const onSubmit = handleSubmit(async (data) => {
    if (effectiveBotId < 1) {
      toast?.current?.show({
        life: 4000,
        severity: "warn",
        summary: "Atenção",
        detail: "Nenhum bot selecionado."
      });
      return;
    }

    const components: ComponentDto[] = [];

    const hasVars = (txt?: string) => /{{\s*\d+\s*}}/.test(txt ?? "");
    const countVars = (txt?: string) =>
      new Set((txt ?? "").match(/{{\s*(\d+)\s*}}/g) ?? []).size;

    if (header === "TEXT") {
      const headerText = (data.headerText ?? "").trim();

      if (headerText) {
        const headerComponent: ComponentDto = {
          type: "HEADER",
          format: "TEXT",
          text: headerText
        };

        const hasHeaderVar = /{{\s*\d+\s*}}/.test(headerText);

        if (hasHeaderVar) {
          const sample = (data.headerSamples?.[0]?.value ?? "").trim();

          if (!sample) {
            toast?.current?.show({
              life: 4000,
              severity: "error",
              summary: "Erro",
              detail: "Preencha a amostra da variável do header ({{1}})."
            });
            return;
          }

          headerComponent.example = { header_text: [sample] };
        }

        components.push(headerComponent);
      }
    }

    const bodyComponent: ComponentDto = {
      type: "BODY",
      text: data.body.trim()
    };

    if (hasVars(bodyComponent.text)) {
      const needed = countVars(bodyComponent.text);

      const bodyExamples = (data.bodySamples ?? [])
        .map((s) => (s.value ?? "").trim())
        .filter(Boolean)
        .slice(0, needed);

      if (bodyExamples.length === needed) {
        bodyComponent.example = { body_text: [bodyExamples] };
      }
    }

    components.push(bodyComponent);

    const footer = data.footerText?.trim();
    if (footer) {
      components.push({
        type: "FOOTER",
        text: footer
      });
    }

    if (data.buttons?.length) {
      const mappedButtons: ButtonDto[] = data.buttons.map((b) => {
        const type = (b.type ?? "").trim();

        if (type === "QUICK_REPLY") {
          return { type: "QUICK_REPLY", text: b.text.trim() };
        }

        if (type === "URL") {
          return {
            type: "URL",
            text: b.text.trim(),
            url: (b.url ?? "").trim()
          };
        }

        return {
          type: "COPY_CODE",
          example: [(b.code ?? "").trim()]
        };
      });

      components.push({
        type: "BUTTONS",
        buttons: mappedButtons
      });
    }

    const templatePayload: TemplateInputDto = {
      name: data.name,
      category: data.category,
      language: data.language,
      components
    };

    const payload = {
      ...templatePayload,
      message_send_ttl_seconds:
        data.category === "UTILITY" ? data.message_send_ttl_seconds : null
    };

    setLoadingSubmit(true);

    try {
      const res =
        mode === "edit" && template?.id
          ? await editTemplate(effectiveBotId, template.id, payload)
          : await createTemplate(effectiveBotId, payload);

      await onSuccess?.();
      dispatch(setShowModalCreateTemplate(false));
    } catch (error: any) {
      const backendMessage =
        error?.message ||
        error?.response?.data?.message ||
        (mode === "edit"
          ? "Erro ao editar template"
          : "Erro ao criar template");

      toast?.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro",
        detail: backendMessage
      });
    } finally {
      setLoadingSubmit(false);
    }
  });

  return (
    <Container>
      {loadingSubmit && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <ProgressSpinner
            style={{ width: "100px", height: "100px" }}
            strokeWidth="4"
            fill="var(--surface-ground)"
            animationDuration=".6s"
          />
        </div>
      )}
      <div className="title">
        <div className="icon">
          <img
            src={user.plataform.urlLogo ? user.plataform.urlLogo : spaik}
            style={{ maxWidth: "65px", maxHeight: "65px" }}
          />
        </div>
        <div className="text">
          <Text s="16px" weight={600} color="#0A043C">
            Criar Modelo de mensagem
          </Text>
          <Text s="12px" lh="18px" color="#544F77" weight={400}>
            Configure seu template para a WhatsApp Business API
          </Text>
        </div>
      </div>

      <Form as="form">
        <div className="fields">
          <div className="field">
            Nome do template
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <InputText
                  {...field}
                  className="input"
                  placeholder="Ex: confirmacao_pedido"
                />
              )}
            />
            {errors.name?.message ? (
              <div
                className="warn"
                style={{ color: "#AA3939", fontSize: "14px" }}
              >
                {errors.name.message}
              </div>
            ) : (
              <div className="warn">
                Use apenas letras minúsculas, números e underscores
              </div>
            )}
          </div>

          <div className="field">
            Categoria
            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <Dropdown
                  style={{
                    width: "98%",
                    borderRadius: "10px",
                    border: "1px solid #e5e7eb",
                    backgroundColor: "#fafafa"
                  }}
                  value={field.value}
                  onChange={(e) => {
                    const next = e.value;

                    field.onChange(next);

                    if (next !== "UTILITY") {
                      setValue("message_send_ttl_seconds", null);
                    }
                  }}
                  options={categoryOptions}
                  placeholder="Selecione uma categoria"
                />
              )}
            />
            {errors.category?.message ? (
              <div style={{ color: "#AA3939" }}>{errors.category.message}</div>
            ) : null}
          </div>

          <div className="field">
            Idioma
            <Controller
              name="language"
              control={control}
              render={({ field }) => (
                <Dropdown
                  style={{
                    width: "98%",
                    borderRadius: "10px",
                    border: "1px solid #e5e7eb",
                    backgroundColor: "#fafafa"
                  }}
                  value={field.value}
                  onChange={(e) => field.onChange(e.value)}
                  options={languageOptions}
                  placeholder="Selecione um idioma"
                />
              )}
            />
            {errors.language?.message ? (
              <div style={{ color: "#AA3939" }}>{errors.language.message}</div>
            ) : null}
          </div>

          <div className="field">
            <div className="optionals">
              <div>Header</div>
              <div className="optional">• Opcional</div>
            </div>
            <div className="headers">
              <div
                className="header"
                onClick={() => toggleHeader("TEXT")}
                style={{
                  outline: header === "TEXT" ? "1px solid #2624CD" : "none",
                  borderRadius: "10px"
                }}
              >
                <img src={IconText} className="icon" />
                Text
              </div>
              <div
                className="header"
                // onClick={() => toggleHeader("IMAGE")}
                style={{
                  outline: header === "IMAGE" ? "1px solid #2624CD" : "none",
                  borderRadius: "10px",
                  cursor: "default",
                  opacity: 0.5
                }}
              >
                <img src={IconImage} className="icon" />
                Image
              </div>
              <div
                className="header"
                // onClick={() => toggleHeader("VIDEO")}
                style={{
                  outline: header === "VIDEO" ? "1px solid #2624CD" : "none",
                  borderRadius: "10px",
                  cursor: "default",
                  opacity: 0.5
                }}
              >
                <img src={IconVideo} className="icon" />
                Video
              </div>
              <div
                className="header"
                // onClick={() => toggleHeader("DOCUMENT")}
                style={{
                  outline: header === "DOCUMENT" ? "1px solid #2624CD" : "none",
                  borderRadius: "10px",
                  cursor: "default",
                  opacity: 0.5
                }}
              >
                <img src={IconFile} className="icon" />
                File
              </div>
            </div>
          </div>

          {header === "TEXT" ? (
            <div className="field">
              <div className="optionals">
                <div>Cabeçalho</div>
                <div className="optional">• Opcional</div>
              </div>
              <Controller
                name="headerText"
                control={control}
                render={({ field }) => (
                  <InputText
                    {...field}
                    className="input"
                    placeholder="Ex: Olá {{1}}, seu pedido foi confirmado!"
                  />
                )}
              />
              <div
                className="addVariable"
                onClick={() => addVarTo("headerText", "headerSamples")}
              >
                + Adicionar variável
              </div>
            </div>
          ) : null}

          <div className="field">
            Corpo
            <Controller
              name="body"
              control={control}
              render={({ field }) => (
                <TextArea
                  {...field}
                  className="textArea"
                  placeholder="Digite o conteúdo principal da mensagem...!"
                />
              )}
            />
            {errors.body?.message ? (
              <div style={{ color: "#AA3939" }}>{errors.body.message}</div>
            ) : null}
            <div
              className="addVariable"
              onClick={() => addVarTo("body", "bodySamples")}
            >
              + Adicionar variável
            </div>
          </div>

          <div className="field">
            <div className="optionals">
              <div>Rodapé</div>
              <div className="optional">• Opcional</div>
            </div>
            <Controller
              name="footerText"
              control={control}
              render={({ field }) => (
                <InputText
                  {...field}
                  className="input"
                  placeholder="Ex: Responda SAIR para cancelar"
                />
              )}
            />
          </div>

          {watch("category") === "UTILITY" ? (
            <div className="field">
              <div className="optionals">
                <div>Tempo de validade</div>
                <div className="optional">• Opcional</div>
              </div>
              <Controller
                name="message_send_ttl_seconds"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    style={{
                      width: "98%",
                      borderRadius: "10px",
                      border: "1px solid #e5e7eb",
                      backgroundColor: "#fafafa"
                    }}
                    value={field.value}
                    onChange={(e) => field.onChange(e.value)}
                    options={validityMinutesOptions}
                    placeholder="Selecione um tempo de validade"
                  />
                )}
              />
              <div className="warn">
                Tempo de validade é o tempo limite que a Meta tenta entregar
                essa mensagem. Após esse prazo, ela é descartada.
              </div>
            </div>
          ) : null}

          <ExampleVariables>
            <div className="titleVariables">Amostra de variáveis</div>
            <div className="textVariables">
              Inclua amostras de todas as variáveis na sua mensagem para ajudar
              a Meta a analisar seu modelo. Para fins de proteção de
              privacidade, lembre-se de não incluir informações do cliente.
            </div>

            {(() => {
              const exampleValuePlaceholder = (index: number) =>
                `Ex: valor do {{${index + 1}}}`;

              return (
                <>
                  {header === "TEXT" && headerSampleFields.length > 0
                    ? headerSampleFields.map((f, i) => (
                        <div className="exampleVariables" key={f.id}>
                          <div className="firstName">
                            <div
                              className="example"
                              style={{
                                display: "flex",
                                gap: "8px",
                                alignItems: "center"
                              }}
                            >
                              <span style={{ width: "50px" }}>Header</span>
                              <span>{`{{${i + 1}}}`}</span>
                            </div>

                            <div className="arrow">
                              <i className="pi pi-arrow-right" />
                            </div>
                          </div>

                          <Controller
                            name={`headerSamples.${i}.value`}
                            control={control}
                            render={({ field }) => (
                              <InputText
                                {...field}
                                placeholder={exampleValuePlaceholder(i)}
                              />
                            )}
                          />
                          <i
                            className="pi pi-times"
                            onClick={() =>
                              removeVarAt(
                                "headerText",
                                "headerSamples",
                                replaceHeaderSamples,
                                i
                              )
                            }
                            style={{
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center"
                            }}
                          />
                        </div>
                      ))
                    : null}

                  {bodySampleFields.length > 0
                    ? bodySampleFields.map((f, i) => (
                        <div className="exampleVariables" key={f.id}>
                          <div className="firstName">
                            <div
                              className="example"
                              style={{
                                display: "flex",
                                gap: "8px",
                                alignItems: "center"
                              }}
                            >
                              <span style={{ width: "50px" }}>Body</span>
                              <span>{`{{${i + 1}}}`}</span>
                            </div>

                            <div className="arrow">
                              <i className="pi pi-arrow-right" />
                            </div>
                          </div>

                          <Controller
                            name={`bodySamples.${i}.value`}
                            control={control}
                            render={({ field }) => (
                              <InputText
                                {...field}
                                placeholder={exampleValuePlaceholder(i)}
                              />
                            )}
                          />
                          <i
                            className="pi pi-times"
                            onClick={() =>
                              removeVarAt(
                                "body",
                                "bodySamples",
                                replaceBodySamples,
                                i
                              )
                            }
                            style={{
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center"
                            }}
                          />
                        </div>
                      ))
                    : null}
                </>
              );
            })()}
          </ExampleVariables>

          <div className="field">
            <div className="optionals">
              <div>Botões</div>
              <div className="optional">• Opcional</div>
            </div>
            <div
              className="addButton"
              onClick={() => appendButton({ type: "QUICK_REPLY", text: "" })}
            >
              + Adicionar botão
            </div>
          </div>

          {buttonFields.map((btn, index) => {
            const currentType = watch(`buttons.${index}.type`);

            return (
              <div className="field" key={btn.id}>
                <ButtonAdded>
                  <div className="titleButtonAdded">
                    <div>Botão</div>
                    <i
                      className="pi pi-times"
                      onClick={() => removeButton(index)}
                      style={{ cursor: "pointer" }}
                    />
                  </div>

                  <div className="contentButtonAdded">
                    <div className="buttonAdded">
                      <div className="type">Tipo</div>

                      <Controller
                        name={`buttons.${index}.type`}
                        control={control}
                        render={({ field }) => (
                          <Dropdown
                            style={{
                              borderRadius: "10px",
                              border: "1px solid #e5e7eb",
                              backgroundColor: "#fafafa"
                            }}
                            value={field.value}
                            options={buttonTypeOptions}
                            onChange={(e) => {
                              const next = e.value as ButtonTypeValue;

                              field.onChange(next);

                              if (next !== "URL")
                                setValue(`buttons.${index}.url`, "");
                              if (next !== "COPY_CODE")
                                setValue(`buttons.${index}.code`, "");
                            }}
                            placeholder="Selecione"
                          />
                        )}
                      />
                    </div>

                    {currentType !== "COPY_CODE" ? (
                      <div className="textButtonAddedQuick">
                        <div className="type">Texto do botão</div>
                        <Controller
                          name={`buttons.${index}.text`}
                          control={control}
                          render={({ field }) => (
                            <InputText
                              {...field}
                              className="inputButton"
                              placeholder={
                                currentType === "URL"
                                  ? "Ex: Acessar site"
                                  : "Resposta rápida"
                              }
                            />
                          )}
                        />
                      </div>
                    ) : null}

                    {currentType === "COPY_CODE" ? (
                      <div className="textButtonAdded">
                        <div className="type">Código da oferta</div>
                        <Controller
                          name={`buttons.${index}.code`}
                          control={control}
                          render={({ field }) => (
                            <InputText
                              value={field.value ?? ""}
                              onChange={(e) => {
                                const raw = e.target.value ?? "";
                                const cleaned = raw.replace(
                                  /[^A-Za-z0-9]/g,
                                  ""
                                );
                                field.onChange(cleaned);
                              }}
                              className="inputButton"
                              placeholder="Ex: FALL25"
                            />
                          )}
                        />
                      </div>
                    ) : null}

                    {currentType === "URL" ? (
                      <div className="textButtonAdded">
                        <div className="type">URL</div>
                        <Controller
                          name={`buttons.${index}.url`}
                          control={control}
                          render={({ field }) => (
                            <InputText
                              {...field}
                              className="inputButton"
                              placeholder="Ex: https://site.com/oferta"
                            />
                          )}
                        />
                      </div>
                    ) : null}
                  </div>
                </ButtonAdded>
              </div>
            );
          })}
        </div>
        <div className="buttonsEnd">
          <Button
            style={{
              background: "#fafafa",
              color: "#29303D",
              border: "1px solid #e5e7eb"
            }}
            onClick={() => dispatch(setShowModalCreateTemplate(false))}
          >
            Voltar
          </Button>

          <Button
            style={{
              background: user.plataform.mainColor
                ? user.plataform.mainColor
                : "#2624CD",
              color: "#fff"
            }}
            onClick={onSubmit}
          >
            Enviar para análise
          </Button>
        </div>
      </Form>
    </Container>
  );
};

export default UpdateWhatsAppTemplate;
